import React, { useState } from "react";

import {
  animated,
  useTransition,
  config as springConfig,
} from "@react-spring/web";
import { isEmpty } from "lodash/fp";

import useMeasure from "@app/common/hooks/useMeasure";
import useTimeout from "@app/common/hooks/useTimeout";

import Stack from "@rui/Stack";
import Stepper from "@rui/Stepper";
import { Img } from "@rui/styles";

import {
  CarouselPanel,
  CarouselPanelTitle,
  CarouselStepperInnerContainer,
  CarouselStepperOuterContainer,
  ImageCarouselPanel,
  ImageContainer,
  ImageShim,
  VideoCarouselPanel,
} from "./styles";

export type VideoPanel = { video: string; caption: string };
export type ImagePanel = { image: string; caption: string };

export type AuthCarouselPanels = ImagePanel[] | VideoPanel[];

const isVideoCarousel = (
  panels: AuthCarouselPanels
): panels is VideoPanel[] => {
  return "video" in panels[0];
};

const AnimatedStack = animated(Stack);

const VideoCarousel = ({
  activeIndex,
  panels,
}: {
  activeIndex: number;
  panels: VideoPanel[];
}) => {
  const [containerRef, , { innerHeight }] = useMeasure();
  const transitions = useTransition(activeIndex, {
    from: { transform: "translate3d(100%, 0, 0)" },
    enter: { transform: "translate3d(0%, 0, 0)" },
    leave: { transform: "translate3d(-100%, 0, 0)" },
    config: { ...springConfig.slow, mass: 3 },
  });

  if (isEmpty(panels)) return null;

  return (
    <Stack flex={1} height="100%" width="100%" maxWidth="100%">
      <VideoCarouselPanel variant="center" ref={containerRef}>
        {transitions((style, i) => (
          <AnimatedStack
            style={{
              ...style,
              position: "absolute",
              top: 0,
              width: innerHeight * 1.25,
              maxWidth: "100%",
              maxHeight: innerHeight,
            }}
          >
            <ImageContainer>
              <video autoPlay muted loop>
                <source src={panels[i].video} type="video/mp4" />
              </video>
            </ImageContainer>
          </AnimatedStack>
        ))}
      </VideoCarouselPanel>
    </Stack>
  );
};

const ImageCarousel = ({
  activeIndex,
  panels,
}: {
  activeIndex: number;
  panels: ImagePanel[];
}) => {
  const transitions = useTransition(activeIndex, {
    from: { transform: "translate3d(100%, 0, 0)" },
    enter: { transform: "translate3d(0%, 0, 0)" },
    leave: { transform: "translate3d(-100%, 0, 0)" },
    config: { ...springConfig.slow, mass: 3 },
  });

  if (isEmpty(panels)) return null;

  return (
    <ImageCarouselPanel>
      {/* Transparent image that holds the space on the DOM for the image carousel */}
      <ImageShim src={panels[0].image} />
      {transitions((style, i) => (
        <animated.div
          style={{
            ...style,
            position: "absolute",
            bottom: 0,
            maxHeight: "100%",
          }}
        >
          <ImageContainer>
            <Img
              src={panels[i].image}
              alt={panels[i].caption}
              sx={{ maxWidth: "100%" }}
            />
          </ImageContainer>
        </animated.div>
      ))}
    </ImageCarouselPanel>
  );
};

const AuthLandingCarousel = ({ panels }: { panels: AuthCarouselPanels }) => {
  const [activePanelIndex, setActivePanelIndex] = useState<number>(0);

  const activePanel = panels[activePanelIndex];
  const nextPanel = () => setActivePanelIndex((i) => (i + 1) % panels.length);

  useTimeout(nextPanel, 8000, panels.length > 1, [activePanelIndex]);

  return (
    <CarouselPanel variant="apart">
      <CarouselStepperOuterContainer>
        <CarouselStepperInnerContainer
          variant="center"
          visibility={panels.length > 1 ? "visible" : "hidden"}
        >
          <Stepper
            activeStep={activePanelIndex}
            onClickStep={setActivePanelIndex}
            steps={panels.length}
            activeStepColor="#fff"
            inactiveStepColor="shades.secondary.100"
            sx={{ transition: "all 0.5s ease" }}
          />
        </CarouselStepperInnerContainer>
        <CarouselPanelTitle>{activePanel.caption}</CarouselPanelTitle>
      </CarouselStepperOuterContainer>
      {isVideoCarousel(panels) ? (
        <VideoCarousel activeIndex={activePanelIndex} panels={panels} />
      ) : (
        <ImageCarousel activeIndex={activePanelIndex} panels={panels} />
      )}
    </CarouselPanel>
  );
};

export default AuthLandingCarousel;
